import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { RotateCcw, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Clock, List, LayoutList, Package, DollarSign, FileText, UserCheck } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VENTES_REF_INPUT_CLASS,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const RetoursVentesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ventes.pages.retoursVentesPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'completed', label: p('status_completed'), icon: Package, style: 'bg-green-500/20 text-green-400 border-green-500/30' }
  ];

  const reasonOptions = [
    { value: 'defective', label: p('reason_defective') },
    { value: 'wrong_item', label: p('reason_wrong_item') },
    { value: 'customer_change_mind', label: p('reason_customer_change_mind') },
    { value: 'damaged', label: p('reason_damaged') },
    { value: 'other', label: p('reason_other') }
  ];

  const refundOptions = [
    { value: 'cash', label: p('refund_cash') },
    { value: 'bank_transfer', label: p('refund_bank_transfer') },
    { value: 'mada', label: p('refund_mada') },
    { value: 'credit_note', label: p('refund_credit_note') },
    { value: 'exchange', label: p('refund_exchange') }
  ];

  const mainTabs = [
    { id: 'all_returns', name: p('mainTab_allReturns'), icon: LayoutGrid },
    { id: 'par_statut', name: p('mainTab_byStatus'), icon: AlertCircle },
    { id: 'par_motif', name: p('mainTab_byReason'), icon: FileText }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'pending', name: p('quickFilter_pending'), icon: Clock },
    { id: 'approved', name: p('quickFilter_approved'), icon: CheckCircle },
    { id: 'completed', name: p('quickFilter_completed'), icon: Package }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      const isClickOnReasonButton = event.target.closest('[data-reason-dropdown-button]');
      const isClickInReasonDropdown = event.target.closest('[data-reason-dropdown]');
      if (!isClickOnReasonButton && !isClickInReasonDropdown) {
        setShowReasonDropdown(false);
      }
      
      const isClickOnRefundButton = event.target.closest('[data-refund-dropdown-button]');
      const isClickInRefundDropdown = event.target.closest('[data-refund-dropdown]');
      if (!isClickOnRefundButton && !isClickInRefundDropdown) {
        setShowRefundDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [returns, setReturns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  const { settings } = useSettings();
  const generateReturnRef = useVentesEntityReference('salesReturn', returns);
  
  const [formTab, setFormTab] = useState('return');
  
  const [formData, setFormData] = useState({ 
    return_number: '',
    customer_id: '',
    customer_name: '',
    original_order_id: '',
    original_order_number: '',
    return_date: '',
    status: 'pending',
    return_reason: 'defective',
    return_amount: '',
    refund_method: 'cash',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [showRefundDropdown, setShowRefundDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_returns');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const reasonDropdownRef = useRef(null);
  const refundDropdownRef = useRef(null);
  
  const returnFormTabs = useMemo(() => ([
    { id: 'return', label: p('section_return_info'), icon: RotateCcw, iconColor: 'text-emerald-400' },
    { id: 'settlement', label: p('section_refund_notes'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'return_number', label: p('col_return_number'), required: true },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'return_date', label: p('col_return_date') },
      { key: 'original_order_number', label: p('col_origin_order') },
      { key: 'return_amount', label: p('col_amount') },
      { key: 'return_reason', label: p('col_reason') },
      { key: 'refund_method', label: p('col_refund') },
      { key: 'status', label: p('col_status') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadReturns(); loadCustomers(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReturns = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/retours');
      const rows = response?.data || response || [];
      setReturns(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading sales returns:', error);
      setReturns([]);
    }
  };

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await localApiAdapter.get('/facturation/clients');
      setAvailableCustomers(response?.data || response || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setAvailableCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleCustomerSelection = (customerId) => {
    if (!customerId) {
      setFormData((prev) => ({ ...prev, customer_id: '', customer_name: '' }));
      return;
    }
    const customer = availableCustomers.find((c) => c.id.toString() === customerId.toString());
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        customer_id: customerId.toString(),
        customer_name: customer.company_name || customer.name || '',
      }));
    }
  };

  const stats = {
    total: returns.length,
    pending: returns.filter(r => r.status === 'pending').length,
    approved: returns.filter(r => r.status === 'approved').length,
    completed: returns.filter(r => r.status === 'completed').length,
    totalAmount: returns.reduce((sum, r) => sum + (parseFloat(r.return_amount) || 0), 0)
  };


  const returnDistribution = {
    all_returns: {
      title: p('dist_allReturnsTitle'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_statut: {
      title: p('dist_byStatusTitle'),
      data: [
        { filterKey: 'in_progress', name: p('dist_inProgress'), count: stats.pending + stats.approved, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'finished', name: p('dist_finished'), count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_motif: {
      title: p('dist_byReasonTitle'),
      data: (() => {
        const reasonCounts = {
          defective: returns.filter(r => r.return_reason === 'defective').length,
          wrong_item: returns.filter(r => r.return_reason === 'wrong_item').length,
          customer_change_mind: returns.filter(r => r.return_reason === 'customer_change_mind').length
        };
        return [
          { filterKey: 'defective', name: p('dist_defective'), count: reasonCounts.defective, color: "bg-gradient-to-r from-red-500 to-red-600" },
          { filterKey: 'wrong_item', name: p('dist_wrongItem'), count: reasonCounts.wrong_item, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
          { filterKey: 'customer_change_mind', name: p('dist_changeMind'), count: reasonCounts.customer_change_mind, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" }
        ];
      })()
    }
  };

  const distributionInfo = returnDistribution[mainViewMode] || returnDistribution.all_returns;

  const returnStatusFilterOptions = useMemo(() => statusOptions.map((o) => ({ value: o.value, label: o.label })), [statusOptions]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('return_number'),
    textColumnRule('customer_name'),
    textColumnRule('return_date', (r) => (r.return_date ? fmtDate(r.return_date) : '')),
    textColumnRule('original_order_number'),
    textColumnRule('return_amount', (r) => String(r.return_amount ?? '')),
    textColumnRule('return_reason', (r) => reasonOptions.find((o) => o.value === r.return_reason)?.label || r.return_reason || ''),
    exactColumnRule('status', (r) => r.status ?? ''),
    textColumnRule('refund_method', (r) => refundOptions.find((o) => o.value === r.refund_method)?.label || r.refund_method || ''),
  ]), [reasonOptions, refundOptions]);

  const baseFilteredReturns = useMemo(() => returns.filter(r => {
    if (!matchesVentesListDateRange(r.return_date, dateRange)) return false;
    if (quickFilter !== 'all' && r.status !== quickFilter) return false;
    if (searchTerm && !r.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_returns':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'approved' && r.status !== 'approved') return false;
          if (activeDistributionFilter === 'completed' && r.status !== 'completed') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'in_progress' && !['pending', 'approved'].includes(r.status)) return false;
          if (activeDistributionFilter === 'finished' && r.status !== 'completed') return false;
          break;
        case 'par_motif':
          const motifMap = { defective: 'defective', wrong_item: 'wrong_item', customer_change_mind: 'customer_change_mind' };
          if (r.return_reason !== motifMap[activeDistributionFilter]) return false;
          break;
      }
    }
    return true;
  }), [returns, quickFilter, searchTerm, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredReturns = useMemo(
    () => applyColumnFilters(baseFilteredReturns, filters, columnFilterRules),
    [baseFilteredReturns, filters, columnFilterRules],
  );

  const filteredReturnIds = useMemo(() => filteredReturns.map((r) => r.id), [filteredReturns]);
  const displayedReturns = useMemo(
    () => sliceVentesListItems(filteredReturns, ventesConfig.itemsPerPage),
    [filteredReturns, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredReturnIds, selectedRecords),
    [filteredReturnIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const resolvedStatus = !editingRecord && ventesConfig.requireReturnApproval
        ? 'pending'
        : formData.status;
      const returnNumber = resolveVentesEntityReference(
        settings,
        'salesReturn',
        returns,
        formData.return_number,
      );
      const submitData = { 
        return_number: returnNumber,
        customer_id: parseInt(formData.customer_id) || null,
        customer_name: formData.customer_name,
        original_order_id: parseInt(formData.original_order_id) || null,
        original_order_number: formData.original_order_number,
        return_date: formData.return_date || new Date().toISOString().split('T')[0],
        status: resolvedStatus,
        return_reason: formData.return_reason,
        return_amount: parseFloat(formData.return_amount) || 0,
        refund_method: formData.refund_method,
        notes: formData.notes
      };
      if (editingRecord) {
        await localApiAdapter.put(`/ventes/retours/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'إرجاع' }));
      } else {
        await localApiAdapter.post('/ventes/retours', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'إرجاع' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message || p('save_error_unknown') }));
      return;
    } finally {
      resetForm();
      loadReturns();
    }
  };

  const handleEdit = (record) => {
    setFormTab('return');
    setEditingRecord(record);
    setFormData({ 
      return_number: record.return_number || '',
      customer_id: record.customer_id?.toString() || '',
      customer_name: record.customer_name || '',
      original_order_id: record.original_order_id?.toString() || '',
      original_order_number: record.original_order_number || '',
      return_date: record.return_date || '',
      status: record.status || 'pending',
      return_reason: record.return_reason || 'defective',
      return_amount: record.return_amount?.toString() || '',
      refund_method: record.refund_method || 'cash',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/retours/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'إرجاع' }));
        loadReturns();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('delete_error', { defaultValue: 'تعذّر حذف الإرجاع' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/ventes/retours/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'إرجاع' }));
        setSelectedRecords([]);
        loadReturns();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('bulk_delete_error', { defaultValue: 'تعذّر حذف الإرجاعات المحددة' }));
      }
    }
  };

  const openCreateReturnModal = () => {
    if (!ventesConfig.enableReturns) return;
    setEditingRecord(null);
    setFormTab('return');
    setFormData({
      return_number: ventesConfig.autoGenerateReturnReference !== false ? generateReturnRef() : '',
      customer_id: '',
      customer_name: '',
      original_order_id: '',
      original_order_number: '',
      return_date: '',
      status: 'pending',
      return_reason: 'defective',
      return_amount: '',
      refund_method: ventesConfig.defaultRefundMethod || 'bank_transfer',
      notes: '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      return_number: '',
      customer_id: '',
      customer_name: '',
      original_order_id: '',
      original_order_number: '',
      return_date: '',
      status: 'pending',
      return_reason: 'defective',
      return_amount: '',
      refund_method: ventesConfig.defaultRefundMethod || 'bank_transfer',
      notes: '',
    });
    setEditingRecord(null);
    setFormTab('return');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [{ key: 'return_number', label: p('col_return_number') }, { key: 'customer_name', label: p('col_customer') }, { key: 'return_date', label: p('col_return_date') }, { key: 'return_amount', label: p('col_amount') }, { key: 'return_reason', label: p('col_reason') }, { key: 'status', label: p('col_status') }];
    const fileName = `retours_ventes_${new Date().toISOString().split('T')[0]}`;
    try { exportToCSV(filteredReturns, headers, `${fileName}.csv`); } catch (error) { console.error('Export error:', error); }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      await generateReportPDF(p('report_pdf_title'), {});
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      await generateReportExcel(p('report_excel_title'), p('report_pdf_title'), {});
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [{ key: 'return_number', label: p('col_return_number') }, { key: 'customer_name', label: p('col_customer') }, { key: 'return_date', label: p('col_return_date') }];
    const schema = { requiredFields: ['customer_name', 'return_date'] };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try { await localApiAdapter.post('/ventes/retours', record); successCount++; } catch (err) {}
          }
          await loadReturns();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredReturnIds);
    }
  };
  const handleSelectRecord = (id) => { setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(rId => rId !== id) : [...selectedRecords, id]); };


  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/retours/${recordId}`, { status: newStatus });
      await loadReturns();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total_returns'), value: stats.total, icon: RotateCcw, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      { key: 'approved', label: p('stat_approved'), value: stats.approved, icon: CheckCircle, variant: 'copper' },
      { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: Package, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">{mainTabs.map((tab) => (<button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}><tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span></button>))}</div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>{activeDistributionFilter && (<button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"><XCircle size={14} />{p('reset')}</button>)}</div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <button key={item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
              <div className="flex justify-between items-center mb-2"><h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4><div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div></div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden"><div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }}></div></div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enableReturns ? p('notice_returns_disabled', { defaultValue: 'مرتجعات المبيعات معطّلة — فعّلها من إعدادات المبيعات ← مرتجعات المبيعات.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={RotateCcw}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={ventesConfig.enableReturns ? openCreateReturnModal : undefined}
        createLabel={p('new_return')}
        importExportState={{
          showImportDropdown,
          setShowImportDropdown,
          showExportDropdown,
          setShowExportDropdown,
          importDropdownRef,
          exportDropdownRef,
        }}
        importExportHandlers={{
          onImport: handleImport,
          onImportCSV: handleImport,
          onImportExcel: handleImport,
          onExportCSV: handleExport,
          onExportExcel: handleExportExcel,
          onExportPDF: handleExportPDF,
        }}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  <TableColumnHeaderFilter columnKey="return_number" label={p('col_return_number')} value={filters.return_number || ''} onChange={(v) => setFilter('return_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('customer_name') ? (
                    <TableColumnHeaderFilter columnKey="customer_name" label={p('col_customer')} value={filters.customer_name || ''} onChange={(v) => setFilter('customer_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('return_date') ? (
                    <TableColumnHeaderFilter columnKey="return_date" label={p('col_return_date')} value={filters.return_date || ''} onChange={(v) => setFilter('return_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('original_order_number') ? (
                    <TableColumnHeaderFilter columnKey="original_order_number" label={p('col_origin_order')} value={filters.original_order_number || ''} onChange={(v) => setFilter('original_order_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('return_amount') ? (
                    <TableColumnHeaderFilter columnKey="return_amount" label={p('col_amount')} value={filters.return_amount || ''} onChange={(v) => setFilter('return_amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('return_reason') ? (
                    <TableColumnHeaderFilter columnKey="return_reason" label={p('col_reason')} value={filters.return_reason || ''} onChange={(v) => setFilter('return_reason', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('refund_method') ? (
                    <TableColumnHeaderFilter columnKey="refund_method" label={p('col_refund')} value={filters.refund_method || ''} onChange={(v) => setFilter('refund_method', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={returnStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedReturns.length > 0 ? (
                  displayedReturns.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.return_number}</span><span className="text-white/50 text-xs mt-0.5">{p('created_label')} {record.created_at ? fmtDate(record.created_at) : '-'}</span></div></td>
                      {showCol('customer_name') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.customer_name}</span><span className="text-white/50 text-xs mt-0.5">ID: {record.customer_id || 'N/A'}</span></div></td>
                      ) : null}
                      {showCol('return_date') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><div className="flex items-center gap-1.5 text-white font-semibold"><Clock size={14} className="text-white/50" />{fmtDate(record.return_date)}</div><span className="text-white/50 text-xs mt-0.5">{record.return_date ? p('days_ago', { days: Math.floor((Date.now() - new Date(record.return_date).getTime()) / (1000 * 60 * 60 * 24)) }) : ''}</span></div></td>
                      ) : null}
                      {showCol('original_order_number') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.original_order_number || '-'}</span><span className="text-white/50 text-xs mt-0.5">ID: {record.original_order_id || 'N/A'}</span></div></td>
                      ) : null}
                      {showCol('return_amount') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{formatBrandMoney(record.return_amount || 0)}</span><span className="text-white/50 text-xs mt-0.5">{p('sublabel_to_refund')}</span></div></td>
                      ) : null}
                      {showCol('return_reason') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{reasonOptions.find(r => r.value === record.return_reason)?.label || '-'}</span><span className="text-white/50 text-xs mt-0.5">{p('sublabel_reason')}</span></div></td>
                      ) : null}
                      {showCol('refund_method') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{refundOptions.find(r => r.value === record.refund_method)?.label || '-'}</span><span className="text-white/50 text-xs mt-0.5">{p('method_label')}</span></div></td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><RotateCcw size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_state')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedReturns.length > 0 ? (
              displayedReturns.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><RotateCcw size={20} className="text-emerald-400" />{record.return_number}</h3>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                        />
                        {record.return_date && (<span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{fmtDate(record.return_date)}</span>)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><h5 className="font-medium text-white mb-2">{p('card_return')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_return_number')}</span> {record.return_number}</div><div><span className="font-medium">{p('label_date')}</span> {fmtDate(record.return_date)}</div><div><span className="font-medium">{p('label_amount')}</span> {formatBrandMoney(record.return_amount || 0)}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('card_customer')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_name')}</span> {record.customer_name}</div><div><span className="font-medium">{p('label_id')}</span> {record.customer_id || 'N/A'}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('card_motif')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_reason')}</span> {reasonOptions.find(r => r.value === record.return_reason)?.label || '-'}</div><div><span className="font-medium">{p('label_refund')}</span> {refundOptions.find(r => r.value === record.refund_method)?.label || '-'}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('card_origin')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_return_number')}</span> {record.original_order_number || '-'}</div>{record.notes && (<div className="mt-2 p-2 bg-white/5 rounded"><p className="text-white/60 text-xs">{record.notes}</p></div>)}</div></div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><RotateCcw size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        maxWidth="1100px"
        testId="retours-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><RotateCcw size={18} className="text-emerald-400" />{p('section_return_info')}</h3><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><div><p className="text-white/60 text-sm mb-1">{p('label_return_number_field')}</p><p className="text-white font-medium text-lg">{viewingRecord.return_number}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_date').replace(':', '')}</p><p className="text-white font-medium">{fmtDate(viewingRecord.return_date)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('col_amount')}</p><p className="text-white font-medium text-lg">{formatBrandMoney(viewingRecord.return_amount || 0)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('col_status')}</p><div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status') })} /></div></div></div></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><UserCheck size={18} className="text-blue-400" />{p('section_customer')}</h3><div className="space-y-3"><div><p className="text-white/60 text-sm mb-1">{p('label_name').replace(':', '')}</p><p className="text-white font-medium">{viewingRecord.customer_name}</p></div><div><p className="text-white/60 text-sm mb-1">ID</p><p className="text-white font-medium">{viewingRecord.customer_id || 'N/A'}</p></div></div></div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><FileText size={18} className="text-yellow-400" />{p('section_reason_refund')}</h3><div className="space-y-3"><div><p className="text-white/60 text-sm mb-1">{p('col_reason')}</p><p className="text-white font-medium">{reasonOptions.find(r => r.value === viewingRecord.return_reason)?.label || '-'}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_refund_method')}</p><p className="text-white font-medium">{refundOptions.find(r => r.value === viewingRecord.refund_method)?.label || '-'}</p></div>{viewingRecord.notes && (<div><p className="text-white/60 text-sm mb-1">{p('label_notes')}</p><p className="text-white/80 text-sm bg-white/5 p-3 rounded-lg">{viewingRecord.notes}</p></div>)}</div></div>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
        icon={RotateCcw}
        showSaveButton
        formId="return-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="retours-crud-modal"
        tabs={returnFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
      >
        <form id="return-form" onSubmit={handleSubmit}>
          {formTab === 'return' && (
            <div className="space-y-6">
              <section>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <RotateCcw size={16} className="text-emerald-400" />
                  {p('section_return_info')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_return_number_field')}</label>
                    <input type="text" required value={formData.return_number} onChange={(e) => setFormData({ ...formData, return_number: e.target.value })} className={VENTES_REF_INPUT_CLASS} placeholder={p('placeholder_auto_generated')} dir="ltr" />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_return_date_required')} /></label>
                    <input type="date" required value={formData.return_date} onChange={(e) => setFormData({ ...formData, return_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_status_required')} /></label>
                    <div className="relative" ref={statusDropdownRef}>
                      <button type="button" data-status-dropdown-button="true" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"><span>{statusOptions.find(s => s.value === formData.status)?.label || p('select')}</span><ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} /></button>
                      {showStatusDropdown && ReactDOM.createPortal(<div data-status-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(statusDropdownRef.current && (() => { const rect = statusDropdownRef.current.getBoundingClientRect(); const spaceBelow = window.innerHeight - rect.bottom; const spaceAbove = rect.top; const dropdownHeight = 180; return spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width } : { top: rect.bottom + 8, left: rect.left, width: rect.width }; })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>{statusOptions.map(option => (<button key={option.value} type="button" onClick={() => { setFormData({ ...formData, status: option.value }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium"><option.icon size={16} className="mr-2" />{option.label}</button>))}</div>, document.body)}
                    </div>
                  </div>
                </div>
              </section>
              <section className="pt-2 border-t border-white/10">
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <FileText size={16} className="text-blue-400" />
                  {p('section_origin_order')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_customer_name_required')} /></label>
                    {loadingCustomers ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('select')}</div>
                    ) : (
                      <CustomSelect
                        value={formData.customer_id}
                        onChange={handleCustomerSelection}
                        options={availableCustomers.map((customer) => ({
                          value: customer.id.toString(),
                          label: `${customer.company_name || customer.name || p('col_customer')}${customer.contact_person ? ` — ${customer.contact_person}` : ''}${customer.phone1 ? ` — ${customer.phone1}` : ''}`,
                        }))}
                        placeholder={p('placeholder_customer_name')}
                        searchable
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_origin_order_number')}</label>
                    <input type="text" value={formData.original_order_number} onChange={(e) => setFormData({ ...formData, original_order_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_order_number')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_origin_order_id')}</label>
                    <input type="number" value={formData.original_order_id} onChange={(e) => setFormData({ ...formData, original_order_id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_id')} />
                  </div>
                </div>
              </section>
            </div>
          )}
          {formTab === 'settlement' && (
            <div className="space-y-6">
              <section>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <DollarSign size={16} className="text-green-400" />
                  {p('section_amount_reason')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_return_amount')}</label>
                    <input type="number" step="0.01" value={formData.return_amount} onChange={(e) => setFormData({ ...formData, return_amount: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_decimal')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_return_reason_required')} /></label>
                    <div className="relative" ref={reasonDropdownRef}>
                      <button type="button" data-reason-dropdown-button="true" onClick={() => setShowReasonDropdown(!showReasonDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"><span>{reasonOptions.find(r => r.value === formData.return_reason)?.label || p('select')}</span><ChevronDown size={16} className={`transition-transform ${showReasonDropdown ? 'rotate-180' : ''}`} /></button>
                      {showReasonDropdown && ReactDOM.createPortal(<div data-reason-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(reasonDropdownRef.current && (() => { const rect = reasonDropdownRef.current.getBoundingClientRect(); return { top: rect.bottom + 8, left: rect.left, width: rect.width }; })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>{reasonOptions.map(option => (<button key={option.value} type="button" onClick={() => { setFormData({ ...formData, return_reason: option.value }); setShowReasonDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{option.label}</button>))}</div>, document.body)}
                    </div>
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_refund_method_required')} /></label>
                    <div className="relative" ref={refundDropdownRef}>
                      <button type="button" data-refund-dropdown-button="true" onClick={() => setShowRefundDropdown(!showRefundDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"><span>{refundOptions.find(r => r.value === formData.refund_method)?.label || p('select')}</span><ChevronDown size={16} className={`transition-transform ${showRefundDropdown ? 'rotate-180' : ''}`} /></button>
                      {showRefundDropdown && ReactDOM.createPortal(<div data-refund-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(refundDropdownRef.current && (() => { const rect = refundDropdownRef.current.getBoundingClientRect(); return { top: rect.bottom + 8, left: rect.left, width: rect.width }; })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>{refundOptions.map(option => (<button key={option.value} type="button" onClick={() => { setFormData({ ...formData, refund_method: option.value }); setShowRefundDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">{option.label}</button>))}</div>, document.body)}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" rows={3} placeholder={p('placeholder_notes')} />
                  </div>
                </div>
              </section>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default RetoursVentesPage;


